<script setup lang="ts">
import { useBillingStore } from '~/store/billing'

/**
 * "We've issued your invoice — now what?" — the CONTENT.
 *
 * Placing an order used to end in a toast and a new browser tab: the customer
 * paid somewhere else and came back to a checkout page that looked unchanged,
 * with no way to ask whether the payment had landed. This is that missing
 * screen — it states what is being waited on, checks continuously on its own,
 * takes an explicit "I already paid" nudge, and can be dismissed without
 * losing the order.
 *
 * Presentational: the page owns the order and the websocket; this component
 * asks it to act.
 *
 * Split out of `CheckoutPaymentWaitModal` so the commit sheet can show it as
 * its final step without a second dialog opening over the one the customer is
 * already looking at. The modal is still the surface for a balance TOP-UP,
 * which is reached from the signed-in flow and has no commit sheet around it —
 * so there are two shells and, deliberately, one body.
 */
const props = withDefaults(
  defineProps<{
    /**
     * Whether to draw a close button of its own.
     *
     * The modal needs one. Inside the commit sheet the surrounding chrome
     * already has one, and two would be two answers to "how do I get out".
     */
    showClose?: boolean
    /** `order` = a new instance awaiting payment; `topup` = a balance recharge. */
    mode: 'order' | 'topup'
    invoiceExternalId: string
    /** Invoice total in cents (string). */
    totalCents: string
    /** Entire invoice funds prepaid usage for a new on-demand subscription. */
    initialCreditCents?: string | null
    grossCents?: string | null
    discountCents?: string | null
    /**
     * Subscription balance the server took off the full-term price of an
     * add-to-existing order (cents). The customer paid for that once already;
     * the invoice is the remainder and this is what says so.
     */
    balanceAppliedCents?: string | null
    /**
     * What the minimum recharge added to the invoice above the net charge
     * (cents). Paid with the rest, it stays on the subscription as credit —
     * which is why the total can be more than the charge minus the balance.
     */
    minimumRechargeCreditCents?: string | null
    /**
     * Add-to-existing order: the invoice charges the hours left in the cycle,
     * and the instance renews at `termCents` on `cycleEndsAt`. All three are
     * said on the panel so the amount and the next one are both explained.
     */
    chargedHours?: number | null
    cycleEndsAt?: string | null
    termCents?: string | null
    /** waiting → paid → provisioning are the happy path; failed/expired are ends. */
    state: 'waiting' | 'paid' | 'provisioning' | 'failed' | 'expired'
    checking?: boolean
    /** Epoch ms of the last completed check, for the "checked Xs ago" ticker. */
    lastCheckedAt?: number | null
    /** When an unpaid order stops being payable (ISO). */
    expiresAt?: string | null
    /** Something went wrong on the last check — shown inline, not as a toast. */
    errorMessage?: string | null
    /**
     * The invoice is being opened.
     *
     * "Pay now" is not a link — it mints a single-use WHMCS SSO token first,
     * and one WHMCS call is ~1.15s. With no feedback the button looked inert
     * for over a second on the one click that matters most, so people pressed
     * it again. A `loading` UButton in Nuxt UI v4 is also a DISABLED one,
     * which is exactly what is wanted: the second press cannot mint a second
     * token.
     */
    openingInvoice?: boolean
  }>(),
  { showClose: false, openingInvoice: false },
)

const emit = defineEmits<{
  close: []
  check: []
  'open-invoice': []
}>()

const billing = useBillingStore()

/**
 * Amounts, not strings: the API serialises its bigints, so an order with no
 * coupon carries `discountCents: "0"`, and `"0"` is truthy — which is how a
 * "Discount applied −R$ 0,00" line reached every attach order's panel.
 */
const positive = (cents?: string | null): boolean => {
  if (!cents) return false
  try {
    return BigInt(cents) > 0n
  } catch {
    return false
  }
}
const hasDiscount = computed(() => positive(props.discountCents))
const hasBalanceApplied = computed(() => positive(props.balanceAppliedCents))
const hasMinimumRecharge = computed(() => positive(props.minimumRechargeCreditCents))

/** The renewal date in the billing locale, or null when it cannot be parsed. */
const renewalDateLabel = computed(() => {
  if (!props.cycleEndsAt) return null
  const date = new Date(props.cycleEndsAt)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString(billingLocale as string, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
})
const { t } = useI18n()

// ── "checked Xs ago" ticker ───────────────────────────────────────────────
// A spinner alone cannot distinguish "checking" from "hung"; a number that
// keeps moving is the cheapest possible proof the page is still alive.
const now = ref(Date.now())
let ticker: ReturnType<typeof setInterval> | null = null
onMounted(() => {
  ticker = setInterval(() => (now.value = Date.now()), 1000)
})
onUnmounted(() => {
  if (ticker) clearInterval(ticker)
})

const secondsSinceCheck = computed(() => {
  if (!props.lastCheckedAt) return null
  return Math.max(0, Math.round((now.value - props.lastCheckedAt) / 1000))
})

/** Same locale the rest of billing formats dates in — never the browser's. */
const { billingLocale } = useRuntimeConfig().public
const expiresLabel = computed(() => {
  if (!props.expiresAt) return null
  const date = new Date(props.expiresAt)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleString(billingLocale as string, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
})

const isDone = computed(
  () => props.state === 'paid' || props.state === 'provisioning'
)
const isTerminalFailure = computed(
  () => props.state === 'failed' || props.state === 'expired'
)

const title = computed(() => {
  if (props.state === 'paid' || props.state === 'provisioning') {
    return props.mode === 'topup'
      ? t('billing.page.checkout.paymentModal.topupConfirmedTitle')
      : t('billing.page.checkout.paymentModal.confirmedTitle')
  }
  if (props.state === 'failed') return t('billing.page.checkout.paymentModal.failedTitle')
  if (props.state === 'expired') return t('billing.page.checkout.paymentModal.expiredTitle')
  return props.mode === 'topup'
    ? t('billing.page.checkout.paymentModal.topupWaitingTitle')
    : t('billing.page.checkout.paymentModal.waitingTitle')
})

const description = computed(() => {
  if (isDone.value) {
    return props.mode === 'topup'
      ? t('billing.page.checkout.paymentModal.topupConfirmedDesc')
      : t('billing.page.checkout.paymentModal.confirmedDesc')
  }
  if (props.state === 'failed') return t('billing.page.checkout.paymentModal.failedDesc')
  if (props.state === 'expired') return t('billing.page.checkout.paymentModal.expiredDesc')
  return props.mode === 'topup'
    ? t('billing.page.checkout.paymentModal.topupWaitingDesc')
    : t('billing.page.checkout.paymentModal.waitingDesc')
})

function close() {
  emit('close')
}
</script>

<template>
  <div class="relative">
    <UButton
      v-if="showClose"
      class="absolute right-3 top-3 z-10"
      size="sm"
      variant="ghost"
      color="neutral"
      icon="i-lucide-x"
      :aria-label="$t('billing.page.checkout.paymentModal.close')"
      @click="close"
    />

    <!-- Status crown: one glance tells you whether to keep waiting. -->
    <div
      :class="[
        'flex flex-col items-center gap-3 px-6 pb-5 pt-8 text-center',
        isDone
          ? 'bg-success/10'
          : isTerminalFailure
            ? 'bg-error/10'
            : 'bg-primary/5',
      ]"
    >
      <div class="relative flex size-16 items-center justify-center">
        <!-- Only the live "still waiting" state pulses; a finished state
             that keeps animating reads as unfinished. -->
        <span
          v-if="!isDone && !isTerminalFailure"
          class="absolute inset-0 animate-ping rounded-full bg-primary/20"
        />
        <span
          :class="[
            'relative flex size-16 items-center justify-center rounded-full',
            isDone
              ? 'bg-success/15 text-success'
              : isTerminalFailure
                ? 'bg-error/15 text-error'
                : 'bg-primary/15 text-primary',
          ]"
        >
          <UIcon
            :name="
              isDone
                ? 'i-lucide-check'
                : isTerminalFailure
                  ? 'i-lucide-circle-alert'
                  : 'i-lucide-hourglass'
            "
            class="size-8"
          />
        </span>
      </div>

      <div class="space-y-1">
        <h3 class="text-lg font-semibold">{{ title }}</h3>
        <p class="text-sm text-muted max-w-sm mx-auto">{{ description }}</p>
      </div>
    </div>

    <div class="space-y-4 p-6">
      <!-- What is owed, itemised, so the amount in the payment page matches. -->
      <div class="rounded-lg border border-default divide-y divide-default">
        <div class="flex items-center justify-between gap-3 px-3 py-2.5">
          <span class="text-xs text-muted">
            {{ $t('billing.page.checkout.paymentModal.invoice') }}
          </span>
          <span class="font-mono text-sm">#{{ invoiceExternalId }}</span>
        </div>
        <!-- Itemised only when something came off the gross: a coupon, the
             subscription's own balance, or both. `discountCents` arrives as
             the string "0" on an order with no coupon, which is truthy, so
             the amounts are compared rather than the strings. -->
        <div
          v-if="grossCents && (hasDiscount || hasBalanceApplied || hasMinimumRecharge)"
          class="px-3 py-2.5 space-y-1 text-xs"
        >
          <div class="flex justify-between">
            <span class="text-muted">
              {{
                chargedHours
                  ? $t('billing.page.checkout.summary.chargeToCycleEndShort', { hours: chargedHours })
                  : $t('billing.page.checkout.summary.subscriptionSubtotal')
              }}
            </span>
            <span class="font-mono">{{ billing.formatCents(grossCents) }}</span>
          </div>
          <div v-if="hasDiscount" class="flex justify-between text-success">
            <span>{{ $t('billing.page.checkout.summary.discountApplied') }}</span>
            <span class="font-mono">-{{ billing.formatCents(discountCents!) }}</span>
          </div>
          <div v-if="hasBalanceApplied" class="flex justify-between text-success">
            <span>{{ $t('billing.page.checkout.summary.balanceApplied') }}</span>
            <span class="font-mono">-{{ billing.formatCents(balanceAppliedCents!) }}</span>
          </div>
          <div v-if="hasMinimumRecharge" class="flex justify-between">
            <span class="text-muted">{{ $t('billing.page.checkout.summary.minimumRechargeCredit') }}</span>
            <span class="font-mono">+{{ billing.formatCents(minimumRechargeCreditCents!) }}</span>
          </div>
          <p v-if="hasMinimumRecharge" class="text-[11px] text-muted leading-snug pt-1">
            {{ $t('billing.page.checkout.summary.minimumRechargeCreditExplainer', { amount: billing.formatCents(minimumRechargeCreditCents!) }) }}
          </p>
        </div>
        <div class="flex items-center justify-between gap-3 px-3 py-2.5">
          <span class="text-sm font-medium">
            {{ $t('billing.page.checkout.paymentModal.amountDue') }}
          </span>
          <span class="text-lg font-bold tabular-nums">
            {{ billing.formatCents(totalCents) }}
          </span>
        </div>
        <div
          v-if="initialCreditCents"
          class="px-3 py-2 text-xs text-muted"
        >
          {{ $t('billing.page.checkout.recharge.paymentNote', { amount: billing.formatCents(initialCreditCents) }) }}
        </div>
        <!-- The next charge for a committed subscription. -->
        <div
          v-if="termCents && cycleEndsAt && renewalDateLabel"
          class="px-3 py-2 text-xs text-muted"
        >
          {{ $t('billing.page.checkout.summary.renewsAt', { amount: billing.formatCents(termCents), date: renewalDateLabel }) }}
        </div>
      </div>

      <!-- Live check status. -->
      <div v-if="!isDone && !isTerminalFailure" class="space-y-2">
        <UProgress size="sm" animation="carousel" />
        <div
          class="flex items-center justify-center gap-1.5 text-xs text-muted"
        >
          <UIcon
            name="i-lucide-refresh-cw"
            :class="['size-3.5', checking && 'animate-spin']"
          />
          <span v-if="checking">
            {{ $t('billing.page.checkout.paymentModal.checkingNow') }}
          </span>
          <span v-else-if="secondsSinceCheck != null">
            {{
              $t('billing.page.checkout.paymentModal.checkedAgo', {
                seconds: secondsSinceCheck,
              })
            }}
          </span>
          <span v-else>
            {{ $t('billing.page.checkout.paymentModal.autoChecking') }}
          </span>
        </div>
      </div>

      <UAlert
        v-if="errorMessage"
        color="warning"
        variant="soft"
        icon="i-lucide-triangle-alert"
        :description="errorMessage"
      />

      <div class="flex flex-col gap-2">
        <UButton
          v-if="!isDone"
          block
          size="lg"
          color="primary"
          icon="i-lucide-external-link"
          :loading="openingInvoice"
          @click="emit('open-invoice')"
        >
          {{ $t('billing.page.checkout.paymentModal.payNow') }}
        </UButton>
        <UButton
          v-if="!isDone && !isTerminalFailure"
          block
          variant="outline"
          color="neutral"
          icon="i-lucide-check-check"
          :loading="checking"
          @click="emit('check')"
        >
          {{ $t('billing.page.checkout.paymentModal.alreadyPaid') }}
        </UButton>
        <UButton
          block
          variant="ghost"
          color="neutral"
          @click="close"
        >
          {{
            isDone
              ? $t('billing.page.checkout.paymentModal.close')
              : $t('billing.page.checkout.paymentModal.payLater')
          }}
        </UButton>
      </div>

      <p
        v-if="!isDone && !isTerminalFailure"
        class="text-xs text-muted text-center leading-relaxed"
      >
        {{ $t('billing.page.checkout.paymentModal.closeSafeNote') }}
        <template v-if="expiresLabel">
          {{
            $t('billing.page.checkout.paymentModal.expiresNote', {
              date: expiresLabel,
            })
          }}
        </template>
      </p>
    </div>
  </div>
</template>
